import { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { NutrientReference, RecipeNutrition, RecipeV2Detail } from '../types';
import RecipeV2EmptyState from './RecipeV2EmptyState';
import { useTranslation } from 'react-i18next';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import RecipeOverrideRemovalDialog from './RecipeOverrideRemovalDialog';

const dash = '—';

const formatAmount = (value: number | null | undefined, uom: string | null | undefined) =>
  value == null || !Number.isFinite(value) ? dash : `${value.toFixed(2)}${uom ? ` ${uom}` : ''}`;

// Simple, consistent visual cue for the existing UK RI percentage. This does
// not change nutrient values or introduce a new nutrition classification.
const riColor = (percent: number | null | undefined) => {
  if (percent == null || !Number.isFinite(percent)) return null;
  if (percent >= 20) return 'error.main';
  if (percent > 5) return 'warning.main';
  return 'success.main';
};

const NutritionTab = ({ recipe, onMetadataChange }: { recipe: RecipeV2Detail; onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void }) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canWrite = hasPermissionTo(['RecipeV2ComplianceOverride']);
  const [rows, setRows] = useState<RecipeNutrition[]>(recipe.nutrition ?? []);
  useEffect(() => setRows(recipe.nutrition ?? []), [recipe.nutrition]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [reference, setReference] = useState<NutrientReference[]>([]);
  const [selected, setSelected] = useState<NutrientReference | null>(null);
  const [value, setValue] = useState('');
  const [valueUom, setValueUom] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<RecipeNutrition | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeReason, setRemoveReason] = useState('');

  const refresh = async () => {
    const refreshed = await api.getRecipeDetail(recipe.id);
    setRows(refreshed.nutrition ?? []);
    onMetadataChange?.({ nutrition: refreshed.nutrition ?? [] });
  };

  const openAdd = async () => {
    setDialogError(null);
    try {
      setReference(await api.getNutrients(recipe.franchiseId));
      setIsEditing(false);
      setSelected(null);
      setValue('');
      setValueUom('');
      setNotes('');
      setDialogOpen(true);
    } catch (cause) {
      setReference([]);
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.nutrientLoadError', 'Failed to load nutrients.'), severity: 'error' });
    }
  };

  const openOverride = async (row: RecipeNutrition) => {
    setDialogError(null);
    try {
      const list = reference.length ? reference : await api.getNutrients(recipe.franchiseId);
      if (!reference.length) setReference(list);
      setSelected(list.find(item => item.code === row.code) ?? { nutrientId: 0, code: row.code, name: row.name });
      setValue(String(row.overrideValue ?? row.value ?? ''));
      setValueUom(row.overrideValueUom ?? row.valueUom ?? '');
      setNotes(row.overrideNotes ?? '');
      setIsEditing(true);
      setDialogOpen(true);
    } catch (cause) {
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.nutrientLoadError', 'Failed to load nutrients.'), severity: 'error' });
    }
  };

  const saveOverride = async () => {
    const numericValue = Number(value);
    if (!selected || value.trim() === '' || !Number.isFinite(numericValue) || !notes.trim()) return;
    setSaving(true);
    try {
      await api.overrideNutrition(recipe.id, { nutrientCode: selected.code, value: numericValue, valueUom: valueUom || null, notes: notes.trim() });
      await refresh();
      setDialogOpen(false);
      setFeedback({ message: isEditing ? t('feedback.nutritionOverrideSaved', 'Nutrition override saved.') : t('feedback.nutritionAdded', 'Nutrition override added.'), severity: 'success' });
      setSelected(null);
      setValue('');
      setValueUom('');
      setNotes('');
      setDialogError(null);
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : t('feedback.nutritionSaveError', 'Failed to save nutrition override.'));
    } finally { setSaving(false); }
  };

  const confirmRemove = async () => {
    if (!deleteCandidate || !removeReason.trim()) return;
    setIsRemoving(true);
    try {
      await api.clearNutritionOverride(recipe.id, deleteCandidate.code, removeReason.trim());
      await refresh();
      setFeedback({ message: t('feedback.nutritionOverrideRemoved', 'Nutrition override removed.'), severity: 'success' });
      setDeleteCandidate(null);
      setRemoveReason('');
    } catch (cause) {
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.nutritionRemoveError', 'Failed to remove nutrition override.'), severity: 'error' });
    } finally { setIsRemoving(false); }
  };

  const componentNutritionExists = (recipe.components ?? []).some(component => (component.nutrition?.length ?? 0) > 0);
  const status = rows.length > 0 ? 'Complete' : componentNutritionExists ? 'Partial' : 'Unavailable';
  const statusColor: 'success' | 'warning' | 'default' = status === 'Complete' ? 'success' : status === 'Partial' ? 'warning' : 'default';
  const highlights = ['ENERGY_KCAL', 'FAT', 'SATURATES', 'SUGARS', 'SALT']
    .map(code => rows.find(row => row.code.toUpperCase() === code))
    .filter((row): row is RecipeNutrition => row != null);
  const serving = recipe.yields?.[0];
  const nutritionServing = rows.find(row => row.servingSize != null && row.servingUom);
  const servingSize = nutritionServing
    ? formatAmount(nutritionServing.servingSize, nutritionServing.servingUom)
    : serving?.quantity && serving.uom ? `${serving.quantity} ${serving.uom}` : dash;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        spacing={2}>
        <Stack direction="row"
          spacing={1}
          alignItems="center">
          <Typography variant="textXL">{t('nutrition.title', 'Nutritional Info')}</Typography>
          <Chip label={t('nutrition.recipeDerived', 'Recipe-derived')}
            size="small" />
          <Chip label={status}
            size="small"
            color={statusColor} />
        </Stack>
        {highlights.length > 0 && <Box>
          <Typography align="right"
            color="text.secondary"
            variant="textSM"
            sx={{ mb: 0.5 }}>{t('nutrition.summaryTitle', 'Nutrition Summary (per serving)')}</Typography>
          <Stack direction="row"
            spacing={0.5}
            flexWrap="wrap"
            justifyContent="flex-end">
            {highlights.map(row => {
              const color = riColor(row.referenceIntakePercent) ?? 'divider';
              return <Box key={row.code}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  minWidth: 100,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderTop: '4px solid',
                  borderTopColor: color,
                  borderRadius: 1,
                  textAlign: 'center',
                }}>
                <Typography variant="textXS"
                  color="text.secondary">{row.name}</Typography>
                <Typography fontWeight={700}>{formatAmount(row.perServing, row.valueUom)}</Typography>
                <Typography variant="textXS"
                  sx={{ color, fontWeight: 600 }}>
                  {row.referenceIntakePercent == null || !Number.isFinite(row.referenceIntakePercent)
                    ? dash
                    : `${row.referenceIntakePercent.toFixed(0)}% RI`}
                </Typography>
              </Box>;
            })}
          </Stack>
        </Box>}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
        spacing={2}>
        <TextField label={t('nutrition.servingSize', 'Serving Size')}
          value={servingSize}
          size="small"
          sx={{ width: { xs: '100%', sm: 455 } }}
          InputProps={{ readOnly: true }} />
        {canWrite && (
          <Button size="md"
            startIcon={<Add />}
            onClick={openAdd}>{t('nutrition.add', 'Add nutrient override')}</Button>
        )}
      </Stack>
      {status === 'Partial' && (
        <Alert severity="warning">
          Partial nutrition: some ingredient data is missing or cannot be safely combined.
        </Alert>
      )}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small"
          sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('nutrition.nutrient', 'Nutrient')}</TableCell>
              <TableCell align="right">{t('nutrition.per100g', 'Per 100g')}</TableCell>
              <TableCell align="right">{t('nutrition.perServing', 'Per Serving')}</TableCell>
              <TableCell align="right">{t('nutrition.riPerServing', '% RI per serving')}</TableCell>
              <TableCell>{t('dietary.originalValue', 'Original Value')}</TableCell>
              <TableCell>{t('source')}</TableCell>
              {canWrite && <TableCell align="right">{t('actions.title')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <RecipeV2EmptyState colSpan={canWrite ? 7 : 6}>Nutrition unavailable: no compatible current ingredient nutrition data.</RecipeV2EmptyState>
            ) : rows.map((row: RecipeNutrition, index) => (
              <TableRow key={`${row.code}-${index}`}>
                <TableCell>{row.name}{row.isOverridden && <Chip label={t('nutrition.overridden', 'Overridden')}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ ml: 1 }} />}</TableCell>
                <TableCell align="right">{formatAmount(row.value, row.valueUom)}</TableCell>
                <TableCell align="right">{formatAmount(row.perServing, row.valueUom)}</TableCell>
                <TableCell align="right">
                  {row.referenceIntakePercent == null || !Number.isFinite(row.referenceIntakePercent)
                    ? dash
                    : <Typography component="span"
                      sx={{ color: riColor(row.referenceIntakePercent), fontWeight: 600 }}>
                      {row.referenceIntakePercent.toFixed(1)}%
                    </Typography>}
                </TableCell>
                <TableCell>
                  {row.isOverridden ? formatAmount(row.originalValue, row.originalValueUom) : (
                    <Typography variant="textSM"
                      color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>{row.isOverridden ? t('allergens.sourceManual') : t('nutrition.sourceCalculated', 'Calculated')}</TableCell>
                {canWrite && (
                  <TableCell align="right">
                    <Stack direction="row"
                      spacing={0.5}
                      justifyContent="flex-end">
                      <Button size="md"
                        variant="secondary"
                        startIcon={<Edit fontSize="small" />}
                        onClick={() => openOverride(row)}>{t('nutrition.override', 'Override')}</Button>
                      {row.isOverridden && (
                        <IconButton size="small"
                          color="error"
                          aria-label={`Remove override for ${row.name}`}
                          onClick={() => {
                            setDeleteCandidate(row);
                            setRemoveReason('');
                          }}><Delete fontSize="small" /></IconButton>
                      )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{isEditing ? t('nutrition.overrideTitle', 'Override Nutrient Value') : t('nutrition.override', 'Override')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {dialogError && <Typography color="error">{dialogError}</Typography>}
            <Select value={selected?.code ?? ''}
              displayEmpty
              disabled={isEditing}
              onChange={event => setSelected(reference.find(item => item.code === event.target.value) ?? null)}>
              <MenuItem value=""
                disabled>{t('nutrition.nutrient', 'Nutrient')}</MenuItem>
              {reference.map(item => (
                <MenuItem key={item.code}
                  value={item.code}>{item.name}</MenuItem>
              ))}
            </Select>
            <TextField label={t('nutrition.overrideValue', 'Value')}
              type="number"
              value={value}
              onChange={event => setValue(event.target.value)}
              required />
            <TextField label={t('nutrition.overrideValueUom', 'Unit')}
              value={valueUom}
              onChange={event => setValueUom(event.target.value)} />
            <TextField label={t('nutrition.overrideNotes', 'Reason')}
              value={notes}
              onChange={event => setNotes(event.target.value)}
              required
              multiline
              rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setDialogOpen(false)}>{t('actions.cancel')}</Button>
          <Button variant="primary"
            onClick={saveOverride}
            disabled={!selected || value.trim() === '' || !notes.trim() || saving}>{t('actions.save')}</Button>
        </DialogActions>
      </Dialog>

      <RecipeOverrideRemovalDialog
        open={Boolean(deleteCandidate)}
        title={t('feedback.removeNutritionOverrideConfirmTitle', 'Remove nutrition override?')}
        message={t('feedback.removeNutritionOverrideConfirmMessage', 'This restores the calculated value for this nutrient.')}
        reason={removeReason}
        isProcessing={isRemoving}
        onReasonChange={setRemoveReason}
        onConfirm={confirmRemove}
        onCancel={() => {
          setDeleteCandidate(null);
          setRemoveReason('');
        }}
      />
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </Stack>
  );
};

export default NutritionTab;
